import { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { CROSS_TAB_SYNCED_SLICES, SLICE_NAMES } from './constants';
import { logger } from '@/utils';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';

const CHANNEL_NAME = 'blocksite-redux-sync';
const SYNC_ACTION_SOURCE = '__CROSS_TAB_SYNC__';

const FLAGS_CONFIG_PREFIX = `${SLICE_NAMES.FLAGS_CONFIG}/`;

// optInPreferences is server-authoritative (PROD-12676); syncing it across tabs could clobber
// the user's latest choice with a stale/extension-sourced set, so strip it both ways.
const stripNonSyncableFlags = (action: SyncableAction): SyncableAction => {
  const payload = (action as { payload?: unknown }).payload;
  if (
    typeof action.type === 'string' &&
    action.type.startsWith(FLAGS_CONFIG_PREFIX) &&
    payload &&
    typeof payload === 'object' &&
    'optInPreferences' in (payload as Record<string, unknown>)
  ) {
    const { optInPreferences: _omit, ...rest } = payload as Record<
      string,
      unknown
    >;
    return { ...action, payload: rest };
  }
  return action;
};

interface SyncableAction extends UnknownAction {
  type: string;
  meta?: { source?: string };
}

const LOG_CONTEXT = {
  tags: {
    feature: LOGGER_FEATURE.USER,
    action: LOGGER_ACTION.SYNC_FROM_REMOTE,
  },
};

const SYNCED_ACTION_PREFIXES = CROSS_TAB_SYNCED_SLICES.map(name => `${name}/`);

const isValidAction = (action: unknown): action is SyncableAction => {
  if (!action || typeof action !== 'object') return false;
  const a = action as Record<string, unknown>;
  return typeof a.type === 'string' && a.type.length > 0;
};

const shouldSyncAction = (action: unknown): boolean =>
  isValidAction(action) &&
  SYNCED_ACTION_PREFIXES.some(prefix => action.type.startsWith(prefix));

const isFromCrossTabSync = (action: unknown): boolean =>
  isValidAction(action) && action.meta?.source === SYNC_ACTION_SOURCE;

const canSerialize = (action: unknown): boolean => {
  try {
    JSON.stringify(action);
    return true;
  } catch {
    return false;
  }
};

export const createCrossTabSyncMiddleware = (): {
  middleware: Middleware;
  cleanup: () => void;
} => {
  let channel: BroadcastChannel | null = null;
  let isCleanedUp = false;

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch (error) {
      logger.error('Failed to create BroadcastChannel', error, LOG_CONTEXT);
    }
  }

  const middleware: Middleware = store => {
    if (channel) {
      channel.onmessage = (event: MessageEvent<unknown>) => {
        try {
          if (isCleanedUp) return;

          const action = event.data;
          if (action == null) return;
          if (!isValidAction(action) || !shouldSyncAction(action)) return;

          const sanitized = stripNonSyncableFlags(action);
          store.dispatch({
            ...sanitized,
            meta: { ...sanitized.meta, source: SYNC_ACTION_SOURCE },
          });
        } catch (error) {
          logger.error('Cross-tab sync receive error', error, LOG_CONTEXT);
        }
      };

      channel.onmessageerror = () => {
        logger.error(
          'BroadcastChannel deserialization error',
          null,
          LOG_CONTEXT
        );
      };
    }

    return next => (action: unknown) => {
      const result = next(action);

      if (
        channel &&
        !isFromCrossTabSync(action) &&
        shouldSyncAction(action) &&
        canSerialize(action)
      ) {
        try {
          channel.postMessage(stripNonSyncableFlags(action as SyncableAction));
        } catch (error) {
          logger.error('Cross-tab sync send error', error, LOG_CONTEXT);
        }
      }

      return result;
    };
  };

  const cleanup = (): void => {
    isCleanedUp = true;
    channel?.close();
    channel = null;
  };

  return { middleware, cleanup };
};
